import { useEffect, useState, useRef, useCallback } from "react"
import { useNavigate } from "react-router"
import { Dialog, DialogContent, DialogTitle } from "~/components/ui/dialog"
import { useApi } from "~/hooks/useApi"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command"
import { Skeleton } from "~/components/ui/skeleton"

type Account = { id: string; displayName: string }

export function CommandPalette() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(true)
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const { get } = useApi()

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      // A shortcut for operators, not a reason to sign anyone in: without a
      // Console session (signed in to Firebase but turned away, or on the
      // logged-out docs) it stays off instead of starting a token refresh
      // that /resources/session refuses.
      const response = await get("/resources/command-palette", {
        skipAuthRedirect: true,
      })
      if (!response.ok) {
        setEnabled(false)
        return
      }
      const data = await response.json()
      setEnabled(data.enabled)
      if (data.enabled) {
        setAccounts(data.accounts)
      }
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }

    document.addEventListener("keydown", down)
    return () => {
      document.removeEventListener("keydown", down)
    }
  }, [])

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        const input =
          inputRef.current ||
          (document.querySelector("[cmdk-input]") as HTMLInputElement)
        input?.focus()
      }, 100)
    }
  }, [open])

  if (enabled === false) {
    return null
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="overflow-hidden p-0 top-[20%] translate-y-0 data-[state=closed]:slide-out-to-top-[20%] data-[state=open]:slide-in-from-top-[20%]">
        <DialogTitle className="sr-only" />
        <Command className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          <CommandInput
            ref={inputRef}
            placeholder="Search accounts..."
            autoFocus
            onValueChange={() => {
              requestAnimationFrame(() => {
                listRef.current?.scrollTo({ top: 0 })
              })
            }}
          />
          <CommandList ref={listRef}>
            {loading ? (
              <div className="p-2 space-y-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full rounded-sm" />
                ))}
              </div>
            ) : (
              <>
                <CommandEmpty>No accounts found.</CommandEmpty>
                <CommandGroup heading="Go to account">
                  {accounts.map((account) => (
                    <CommandItem
                      key={account.id}
                      value={account.displayName}
                      onSelect={() => {
                        setOpen(false)
                        navigate(`/accounts/${account.id}/overview`)
                      }}
                    >
                      <span>{account.displayName}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
